import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import type {AsyncVoidFunction} from '@Utils/general.util';
import type {AiPromptBoxContextualMenu} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import type {AiPromptBoxAttachments, LoadingAttachment, PromptAttachment, UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import type {AiPromptBoxIssue} from '@Components/ai-prompt-box/components/ai-prompt-box-issue/ai-prompt-box-issue.types';
import type {PromptSuggestionPills} from '@Components/ai-prompt-box/components/ai-prompt-box-prompt-suggestions/ai-prompt-box-prompt-suggestions.types';
import type {CreditsPillContent} from '@Components/ai-prompt-box/components/ai-prompt-box-credits-pill/ai-prompt-box-credits-pill.types';
import type {DeepPartial} from '@/global';
import type {FooterLeftAction} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import type {AiChatSource} from '@Components/ai-chat-window/ai-chat-window.types';
import type {AiPromptBoxInputField} from '@Components/ai-prompt-box/components/ai-prompt-box-input-field/ai-prompt-box-input-field.types';
import type {ProTipContext} from '@Components/ai-prompt-box/components/ai-prompt-box-pro-tip/ai-prompt-box-pro-tip.types';

export type SelectedContextualMenuItems = Record<string, string[]>;

export interface AiPromptBoxProps {
  id: string;
  initialConfig: AiPromptBox;
  onGenerate: AsyncVoidFunction;
  onRegenerate: AsyncVoidFunction;
  onStop?: AsyncVoidFunction;
  options?: FooterLeftAction[];
  containerClasses?: string;
  blockClasses?: string;
  inputClasses?: string;
  'data-testid'?: string;
  inputTestId?: string;
  submitTestId?: string;
  focus?: boolean;
  onFocus?: VoidFunction;
  showProtips?: boolean;
  proTipContext?: ProTipContext;
  disableAutoActivate?: boolean;
  allowSoftSendDuringUploads?: boolean;
}

export enum AiPromptBoxSize {
  SINGLE_LINE = 'single-line',
  SMALL = 'small',
  MEDIUM = 'medium',
  SMALL_EXTENDED = 'small-extended',
}

export type AiPromptBoxReduxState = Record<string, AiPromptBox | undefined>;

export interface AiPromptBoxInitializationAction {
  id: string;
  value: AiPromptBox;
}

export interface AiPromptBoxUpdateAction {
  id: string;
  value: DeepPartial<AiPromptBox>;
}

export interface AiPromptBoxAddAttachmentsAction {
  id: string;
  attachments: PromptAttachment[];
  loadingAttachments?: LoadingAttachment[];
  prefillPromptIfEmpty?: string;
}

export interface AiPromptBoxAddLoadingAttachmentsAction {
  id: string;
  loadingAttachments: LoadingAttachment[];
}

export interface AiPromptBoxRemoveLoadingAttachmentsAction {
  id: string;
  loadingIds: string[];
}

export interface AiPromptBoxRemoveImageAttachmentsAction {
  id: string;
  hashedFilenames: string[];
}

export interface AiPromptBoxAddPendingImageAttachmentsAction {
  id: string;
  attachments: UserImageAttachment[];
}

export interface AiPromptBoxResolvePendingAttachmentAction {
  id: string;
  localId: string;
  resolved: UserImageAttachment;
}

export interface AiPromptBoxMarkPendingAttachmentFailedAction {
  id: string;
  localId: string;
}

export interface AiPromptBoxMarkPendingAttachmentUploadingAction {
  id: string;
  localId: string;
}

export interface AiPromptBoxRemovePendingAttachmentAction {
  id: string;
  localId: string;
}

export interface AiPromptBoxPreFillAction {
  id: string;
  prompt: string;
}

export enum AiPromptBoxHostSource {
  DESIGN_EDITOR_IMAGE_EDIT = 'de_image_edit',
}

export interface AiPromptBox {
  prompt: string;
  /** The last auto-pre-filled prompt (e.g. a stock search term). A prompt still equal to it counts as untouched and may be pre-filled again. */
  lastPreFilledPrompt?: string;
  placeholder: string;
  isRecordingAudio?: boolean;
  isProcessingAudio?: boolean;
  showInitialAnimation?: boolean;
  contextualMenu?: AiPromptBoxContextualMenu;
  creditsPill?: CreditsPillContent;
  input?: AiPromptBoxInputField;
  promptBoxState: PromptBoxState;
  promptSuggestions?: PromptSuggestionPills;
  attachments?: AiPromptBoxAttachments;
  attachmentLoaders?: LoadingAttachment[];
  size: AiPromptBoxSize;
  error?: AiPromptBoxIssue | null;
  showDisclaimer?: boolean;
  source?: AiChatSource;
  hostSource?: AiPromptBoxHostSource;
  isDailyLimitReached?: boolean;
  showDailyLimitError?: boolean;
  dailyLimitErrorMessage?: string;
  focusKey?: number;
  submitKey?: number;
  isFinishingUploads?: boolean;
  transcription?: AiPromptBoxTranscription;
  pinnedAspectRatio?: string | null;
  pinnedBrandKitId?: string | null;
}

export interface AiPromptBoxTranscription {
  showMic?: boolean;
  permissionDenied?: boolean;
  visualizerBarWidth?: number;
  visualizerGap?: number;
  visualizerFramesPerBar?: number;
}

export enum PromptBoxState {
  IDLE = 'idle',
  ACTIVE = 'active',
  DISABLED = 'disabled',
  LOADING = 'loading',
  STOPPABLE_LOADING = 'stoppable-loading',
  REGENERATE = 'regenerate',
}

/**
 * Where a submission currently is in its lifecycle.
 *
 * - IDLE: not sending; user owns the box.
 * - SOFT_SENDING: user clicked send but uploads are still finishing — message
 *   has NOT crossed into chat history. Stop here keeps the box content; uploads
 *   continue in the background.
 * - HARD_SENDING: message has been handed to onGenerate and is streaming —
 *   message lives in chat history; box has been cleared by the consumer.
 *
 * The boundary between SOFT_SENDING and HARD_SENDING is the call to onGenerate.
 */
export enum AiPromptBoxSendPhase {
  IDLE = 'idle',
  SOFT_SENDING = 'soft-sending',
  HARD_SENDING = 'hard-sending',
}

/** Must stay below the AI Chat prompt boxes' max-width (728px / 760px), or desktop AI Chat gets the mobile layout. */
export const AI_PROMPT_BOX_MOBILE_QUERY: ContainerQueryObject = {
  mobile: {maxWidth: 575},
};
